/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Button,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import classnames from "classnames";
import { useSelector } from "react-redux";

// Services & Hooks
import { useCompanyDashboard } from "../../hooks/useCompanyDashboard";
import { getAllCompanies } from "../../stores/services/company.service";
import { useCustomStylesDropDown } from "../../assets/css/customStyle";
import CardColComponent from "../genericCardComponent/CardColComponent";

// --- Types ---
interface DashboardData {
  monthly_trends?: Array<{
    month: string;
    employee_contribution: number;
    employer_contribution: number;
    profit: number;
  }>;
  investment_distribution?: Array<{
    _id: string;
    count: number;
  }>;
  overview: {
    total_employees: number;
    active_employees: number;
  };
  financial_summary: {
    net_total?: number;
    growth_rate: number;
    total_employee_contribution?: number;
    total_employer_contribution?: number;
    total_profit?: number;
    total_loan_adjustments?: number;
  };
  loan_summary: {
    total_loans: number;
    active_loans: number;
    completed_loans: number;
    total_loan_amount?: number;
    total_remaining?: number;
  };
  recent_activities: {
    recent_joins: Array<{
      name: string;
      folio_no: string;
      joining_date: string;
    }>;
    recent_leaves: Array<{
      name: string;
      folio_no: string;
      leaving_date: string;
    }>;
  };
}

const CompanyDashboard = () => {
  const history = useHistory();
  const theme = useSelector((state: any) => state.ThemeModeReducer.Theme);
  const { customStyles } = useCustomStylesDropDown();
  const email = sessionStorage.getItem("email") || "";

  const [companyOptions, setCompanyOptions] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  // State for hover effect on cards
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  const {
    loading,
    selectedCompany,
    period,
    customDateRange,
    activeTab,
    dashboardData,
    loansAnalytics,
    contributionTrends,
    topEmployees,
    setSelectedCompany,
    setPeriod,
    setCustomDateRange,
    setActiveTab,
    fetchAllAnalytics,
  } = useCompanyDashboard() as any;

  // --- Effects ---
  useEffect(() => {
    const fetchCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const resp = await getAllCompanies({ email, fetch_all: "all" });
        if (resp.data.status === 200) {
          const options = resp.data.data.map((data: any) => ({
            ...data,
            value: data.company_code,
            label: `${data.name_of_company} (${data.company_code})`,
          }));
          setCompanyOptions(options);
        }
      } catch (error) {
        console.error("Failed to load companies:", error);
      } finally {
        setLoadingCompanies(false);
      }
    };
    fetchCompanies();
  }, []);

  // --- Styling Helpers ---
  const isDark = theme === "dark";

  const themeTokens = {
    bg: isDark ? "#25273a" : "#ffffff",
    bgSoft: isDark ? "#3b3b3b" : "#ffffff",
    border: isDark ? "#2b3553" : "rgba(0,0,0,0.08)",
    text: isDark ? "#ffffff" : "#000000",
    textMuted: isDark ? "rgba(255,255,255,0.6)" : "#666",
    accent: "#ba54f5",
    accentSoft: "hsla(291, 64%, 42%, 0.15)",
    focus: "#1d8cf8",
  };

  // Unified Card Style for clean borders and consistent rounding
  const getCardStyle = (isHovered = false) => ({
    background: themeTokens.bg,
    border: `1px solid ${themeTokens.border}`,
    borderRadius: "0.4285rem", // SAME as react-select
    boxShadow: isHovered ? `0 8px 20px ${themeTokens.accentSoft}` : "none",
    transition: "all 0.25s ease",
  });

  const tableHeaderStyle = {
    borderBottom: isDark
      ? "1px solid rgba(255,255,255,0.1)"
      : "1px solid rgba(0,0,0,0.1)",
    color: "#e14eca", // Highlight color for headers
    fontWeight: 600,
    textTransform: "uppercase" as const,
    fontSize: "0.75rem",
    letterSpacing: "0.5px",
  };

  const periodOptions = [
    { value: "7d", label: "Last 7 Days" },
    { value: "30d", label: "Last 30 Days" },
    { value: "90d", label: "Last 90 Days" },
    { value: "1y", label: "Last Year" },
    { value: "all", label: "All Time" },
    { value: "custom", label: "Custom Range" },
  ];

  const numberWithCommas = (value: string | number | undefined) => {
    if (!value) return "0";
    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // --- Chart Data Generators ---
  const getMonthlyTrendsChartData = () => {
    if (!dashboardData?.monthly_trends) return null;
    return {
      labels: dashboardData.monthly_trends.map((t: any) => t.month),
      datasets: [
        {
          label: "Employee Contrib.",
          data: dashboardData.monthly_trends.map(
            (t: any) => t.employee_contribution
          ),
          borderColor: "#1f8ef1",
          backgroundColor: "rgba(31, 142, 241, 0.1)",
          pointBackgroundColor: "#1f8ef1",
          pointRadius: 3,
          fill: true,
          tension: 0.4, // Smooth curves
        },
        {
          label: "Employer Contrib.",
          data: dashboardData.monthly_trends.map(
            (t: any) => t.employer_contribution
          ),
          borderColor: "#00d6b4",
          backgroundColor: "rgba(0, 214, 180, 0.1)",
          pointBackgroundColor: "#00d6b4",
          pointRadius: 3,
          fill: true,
          tension: 0.4,
        },
        {
          label: "Profit",
          data: dashboardData.monthly_trends.map((t: any) => t.profit),
          borderColor: "#ff8d72",
          backgroundColor: "rgba(255, 141, 114, 0.1)",
          pointBackgroundColor: "#ff8d72",
          pointRadius: 3,
          fill: true,
          tension: 0.4,
        },
      ],
    };
  };

  const getInvestmentDistributionData = () => {
    if (!dashboardData?.investment_distribution) return null;
    return {
      labels: dashboardData.investment_distribution.map(
        (i: any) => i._id || "Unspecified"
      ),
      datasets: [
        {
          data: dashboardData.investment_distribution.map((i: any) => i.count),
          backgroundColor: [
            "#1f8ef1",
            "#00d6b4",
            "#ff8d72",
            "#fd5d93",
            "#fcc468",
          ],
          borderWidth: 0,
        },
      ],
    };
  };

  const getLoanTrendsData = () => {
    if (!loansAnalytics?.monthly_disbursement) return null;
    return {
      labels: loansAnalytics.monthly_disbursement.map((m: any) => m.month),
      datasets: [
        {
          label: "Loans Disbursed",
          data: loansAnalytics.monthly_disbursement.map(
            (m: any) => m.loan_count
          ),
          backgroundColor: "#1f8ef1",
          borderRadius: 4,
          yAxisID: "y-axis-0",
          barPercentage: 0.5,
        },
        {
          label: "Total Amount",
          data: loansAnalytics.monthly_disbursement.map(
            (m: any) => m.total_disbursed
          ),
          backgroundColor: "#00d6b4",
          borderRadius: 4,
          yAxisID: "y1",
          barPercentage: 0.5,
        },
      ],
    };
  };

  // --- Chart Options ---
  const commonOptions = {
    maintainAspectRatio: false,
    legend: {
      display: true,
      labels: {
        fontColor: isDark ? "rgba(255,255,255,0.7)" : "#888",
        usePointStyle: true,
        padding: 20,
      },
    },
    tooltips: {
      backgroundColor: isDark ? "#fff" : "#000",
      titleFontColor: isDark ? "#000" : "#fff",
      bodyFontColor: isDark ? "#000" : "#fff",
      cornerRadius: 8,
      displayColors: false,
    },
    scales: {
      yAxes: [
        {
          ticks: {
            fontColor: isDark ? "rgba(255,255,255,0.7)" : "#888",
            beginAtZero: true,
            padding: 10,
          },
          gridLines: {
            color: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
            zeroLineColor: "transparent",
            drawBorder: false,
          },
        },
      ],
      xAxes: [
        {
          ticks: {
            fontColor: isDark ? "rgba(255,255,255,0.7)" : "#888",
            padding: 10,
          },
          gridLines: { display: false, drawBorder: false },
        },
      ],
    },
  };

  const barOptions = {
    ...commonOptions,
    scales: {
      ...commonOptions.scales,
      yAxes: [
        {
          id: "y-axis-0",
          position: "left",
          ticks: { fontColor: isDark ? "rgba(255,255,255,0.7)" : "#888" },
          gridLines: {
            color: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
            zeroLineColor: "transparent",
            drawBorder: false,
          },
        },
        {
          id: "y1",
          position: "right",
          gridLines: { display: false, drawBorder: false },
          ticks: { fontColor: isDark ? "rgba(255,255,255,0.7)" : "#888" },
        },
      ],
    },
  };

  const doughnutOptions = {
    maintainAspectRatio: false,
    cutoutPercentage: 70, // Thinner ring
    legend: {
      display: true,
      position: "bottom",
      labels: {
        fontColor: isDark ? "rgba(255,255,255,0.7)" : "#888",
        usePointStyle: true,
        padding: 20,
      },
    },
  };

  // Helper for KPI Cards
  const KpiCardWrapper = ({
    title,
    subTitle,
    icon,
    color,
    amount = false,
    units = false,
    percentage = false,
    normal_num = false,
    cardId,
  }: any) => (
    <Card
      className="card-stats mb-4 h-100"
      onMouseEnter={() => setHoveredCard(cardId)}
      onMouseLeave={() => setHoveredCard(null)}
      style={getCardStyle(hoveredCard === cardId)}
    >
      <CardBody className="d-flex align-items-center justify-content-between p-4">
        <div>
          <CardColComponent
            title={title}
            subTitle={subTitle}
            amount={amount}
            units={units}
            percentage={percentage}
            normal_num={normal_num}
          />
        </div>
        <div
          className={`icon-shape text-center shadow-sm`}
          style={{
            width: "52px",
            height: "52px",
            lineHeight: "52px",
            borderRadius: "14px", // Softer rounding for icon bg
            flexShrink: 0,
            color:
              color === "primary"
                ? "#1f8ef1"
                : color === "success"
                ? "#00d6b4"
                : color === "info"
                ? "#11cdef"
                : color === "warning"
                ? "#ff8d72"
                : "#fd5d93",
            background:
              color === "primary"
                ? "rgba(31, 142, 241, 0.1)"
                : color === "success"
                ? "rgba(0, 214, 180, 0.1)"
                : color === "info"
                ? "rgba(17, 205, 239, 0.1)"
                : color === "warning"
                ? "rgba(255, 141, 114, 0.1)"
                : "rgba(253, 93, 147, 0.1)",
          }}
        >
          <i className={`tim-icons ${icon}`} style={{ fontSize: "22px" }} />
        </div>
      </CardBody>
    </Card>
  );

  return (
    <>
      <ToastContainer limit={1} />
      <div className="content pt-4">
        <Container fluid>
          {/* --- Header & Controls --- */}
          <Row className="mb-4">
            <Col md="12">
              <Card className="border-0" style={getCardStyle(false)}>
                <CardBody className="p-4">
                  <Row className="align-items-end">
                    <Col lg="4" md="6" className="mb-3 mb-lg-0">
                      <h4 className="title text-primary font-weight-bold mb-3">
                        <i className="fa fa-chart-line mr-2"></i> Dashboard
                      </h4>
                      <label className="text-muted small text-uppercase font-weight-bold">
                        Select Company
                      </label>
                      <Select
                        options={companyOptions}
                        value={selectedCompany}
                        onChange={setSelectedCompany}
                        styles={customStyles}
                        placeholder={
                          loadingCompanies ? "Loading..." : "Select Company..."
                        }
                        isClearable
                      />
                    </Col>

                    <Col lg="3" md="6" className="mb-3 mb-lg-0">
                      <label className="text-muted small text-uppercase font-weight-bold">
                        Time Period
                      </label>
                      <Select
                        options={periodOptions}
                        value={periodOptions.find((p) => p.value === period)}
                        onChange={(e: any) => setPeriod(e?.value || "30d")}
                        styles={customStyles}
                      />
                    </Col>

                    {period === "custom" && (
                      <Col lg="3" md="6" className="mb-3 mb-lg-0">
                        <label className="text-muted small text-uppercase font-weight-bold">
                          Date Range
                        </label>
                        <div className="d-flex">
                          <input
                            type="date"
                            className="form-control mr-2"
                            style={{ borderRadius: "8px" }}
                            value={customDateRange.start_date}
                            onChange={(e) =>
                              setCustomDateRange({
                                ...customDateRange,
                                start_date: e.target.value,
                              })
                            }
                          />
                          <input
                            type="date"
                            className="form-control"
                            style={{ borderRadius: "8px" }}
                            value={customDateRange.end_date}
                            onChange={(e) =>
                              setCustomDateRange({
                                ...customDateRange,
                                end_date: e.target.value,
                              })
                            }
                          />
                        </div>
                      </Col>
                    )}

                    <Col
                      lg={period === "custom" ? "2" : "5"}
                      className="text-right"
                    >
                      <Button
                        color="primary"
                        className="btn-block animation-on-hover shadow-sm"
                        onClick={fetchAllAnalytics}
                        disabled={!selectedCompany || loading}
                        style={{
                          height: "40px",
                          marginTop: "auto",
                          borderRadius: "8px",
                          fontWeight: 600,
                        }}
                      >
                        {loading ? (
                          <i className="fa fa-spinner fa-spin"></i>
                        ) : (
                          "Fetch Data"
                        )}
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* --- Main Dashboard Content --- */}
          {dashboardData && (
            <div className="fade-in">
              {/* 1. High Level Overview */}
              <Row>
                <Col lg="3" md="6" className="mb-3">
                  <KpiCardWrapper
                    title="Total Employees"
                    subTitle={dashboardData.overview.total_employees}
                    icon="icon-badge"
                    color="primary"
                    normal_num
                    cardId="kpi1"
                  />
                </Col>
                <Col lg="3" md="6" className="mb-3">
                  <KpiCardWrapper
                    title="Active Employees"
                    subTitle={dashboardData.overview.active_employees}
                    icon="icon-single-02"
                    color="success"
                    normal_num
                    cardId="kpi2"
                  />
                </Col>
                <Col lg="3" md="6" className="mb-3">
                  <KpiCardWrapper
                    title="Net Total"
                    subTitle={dashboardData.financial_summary.net_total?.toFixed(
                      2
                    )}
                    icon="icon-money-coins"
                    color="info"
                    amount
                    cardId="kpi3"
                  />
                </Col>
                <Col lg="3" md="6" className="mb-3">
                  <KpiCardWrapper
                    title="Growth Rate"
                    subTitle={dashboardData.financial_summary.growth_rate}
                    icon="icon-chart-bar-32"
                    color="warning"
                    percentage
                    cardId="kpi4"
                  />
                </Col>
              </Row>

              {/* 2. Financial Breakdown */}
              <Row className="mb-4">
                <Col md="12">
                  <Card className="border-0" style={getCardStyle()}>
                    <CardHeader className="bg-transparent border-0 pt-3 pl-3">
                      <h5 className="title mb-0">Financial Summary</h5>
                    </CardHeader>
                    <CardBody>
                      <Row>
                        <Col
                          md="3"
                          sm="6"
                          className="mb-3 mb-md-0"
                          style={{
                            borderRight: isDark
                              ? "1px solid rgba(255,255,255,0.05)"
                              : "1px solid rgba(0,0,0,0.05)",
                          }}
                        >
                          <CardColComponent
                            title="Emp Contribution"
                            subTitle={dashboardData.financial_summary.total_employee_contribution?.toFixed(
                              2
                            )}
                            amount
                          />
                        </Col>
                        <Col
                          md="3"
                          sm="6"
                          className="mb-3 mb-md-0"
                          style={{
                            borderRight: isDark
                              ? "1px solid rgba(255,255,255,0.05)"
                              : "1px solid rgba(0,0,0,0.05)",
                          }}
                        >
                          <CardColComponent
                            title="Employer Contribution"
                            subTitle={dashboardData.financial_summary.total_employer_contribution?.toFixed(
                              2
                            )}
                            amount
                          />
                        </Col>
                        <Col
                          md="3"
                          sm="6"
                          className="mb-3 mb-md-0"
                          style={{
                            borderRight: isDark
                              ? "1px solid rgba(255,255,255,0.05)"
                              : "1px solid rgba(0,0,0,0.05)",
                          }}
                        >
                          <CardColComponent
                            title="Total Profit"
                            subTitle={dashboardData.financial_summary.total_profit?.toFixed(
                              2
                            )}
                            amount
                          />
                        </Col>
                        <Col md="3" sm="6">
                          <CardColComponent
                            title="Loan Adjustments"
                            subTitle={dashboardData.financial_summary.total_loan_adjustments?.toFixed(
                              2
                            )}
                            amount
                          />
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              {/* 3. Detailed Tabs */}
              <Row>
                <Col md="12">
                  <Card className="border-0" style={getCardStyle()}>
                    <CardHeader className="border-0">
                      <Nav
                        tabs
                        className="nav-tabs-primary justify-content-start"
                        style={{ borderBottom: "none" }}
                      >
                        {[
                          { id: "overview", label: "Overview" },
                          { id: "trends", label: "Trends" },
                          { id: "loans", label: "Loans" },
                          { id: "employees", label: "Top Employees" },
                          { id: "funds", label: "Funds" },
                        ].map((tab) => (
                          <NavItem key={tab.id} className="mr-2">
                            <NavLink
                              className={classnames({
                                active: activeTab === tab.id,
                              })}
                              onClick={() => setActiveTab(tab.id)}
                              style={{
                                cursor: "pointer",
                                fontWeight: 600,
                                borderRadius: "0.4285rem",
                                padding: "8px 16px",
                                background:
                                  activeTab === tab.id
                                    ? themeTokens.accent
                                    : "transparent",
                                color:
                                  activeTab === tab.id
                                    ? "#fff"
                                    : themeTokens.textMuted,
                                border:
                                  activeTab === tab.id
                                    ? "none"
                                    : `1px solid ${themeTokens.border}`,
                              }}
                            >
                              {tab.label}
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>
                    </CardHeader>

                    <CardBody className="p-4">
                      <TabContent activeTab={activeTab}>
                        {/* --- Tab: Overview --- */}
                        <TabPane tabId="overview">
                          <Row>
                            <Col lg="8" md="12" className="mb-4">
                              <Card
                                className="card-chart h-100 shadow-none"
                                style={{
                                  background: "transparent",
                                  border: isDark
                                    ? "1px solid rgba(255,255,255,0.05)"
                                    : "1px solid rgba(0,0,0,0.05)",
                                  borderRadius: "12px",
                                }}
                              >
                                <CardHeader className="border-0">
                                  <h5 className="card-category text-muted text-uppercase mb-1">
                                    Performance
                                  </h5>
                                  <h4 className="title">
                                    Monthly Contribution
                                  </h4>
                                </CardHeader>
                                <CardBody style={{ height: "320px" }}>
                                  {getMonthlyTrendsChartData() && (
                                    <Line
                                      data={getMonthlyTrendsChartData()}
                                      options={commonOptions}
                                    />
                                  )}
                                </CardBody>
                              </Card>
                            </Col>
                            <Col lg="4" md="12" className="mb-4">
                              <Card
                                className="card-chart h-100 shadow-none"
                                style={{
                                  background: "transparent",
                                  border: isDark
                                    ? "1px solid rgba(255,255,255,0.05)"
                                    : "1px solid rgba(0,0,0,0.05)",
                                  borderRadius: "12px",
                                }}
                              >
                                <CardHeader className="border-0">
                                  <h5 className="card-category text-muted text-uppercase mb-1">
                                    Portfolio
                                  </h5>
                                  <h4 className="title">Fund Distribution</h4>
                                </CardHeader>
                                <CardBody style={{ height: "320px" }}>
                                  {getInvestmentDistributionData() && (
                                    <Doughnut
                                      data={getInvestmentDistributionData()}
                                      options={doughnutOptions}
                                    />
                                  )}
                                </CardBody>
                              </Card>
                            </Col>
                          </Row>

                          {/* Bottom Overview Section */}
                          <Row className="mt-2">
                            <Col md="6">
                              <h5 className="title mb-3">Loan Summary</h5>
                              <div className="table-responsive">
                                <table className="table table-hover align-items-center">
                                  <tbody>
                                    <tr style={{ borderBottom: "none" }}>
                                      <td className="border-0">Total Loans</td>
                                      <td className="text-right border-0">
                                        {dashboardData.loan_summary.total_loans}
                                      </td>
                                    </tr>
                                    <tr style={{ borderBottom: "none" }}>
                                      <td className="border-0">Active Loans</td>
                                      <td className="text-right text-success border-0">
                                        {
                                          dashboardData.loan_summary
                                            .active_loans
                                        }
                                      </td>
                                    </tr>
                                    <tr style={{ borderBottom: "none" }}>
                                      <td className="border-0">Amount</td>
                                      <td className="text-right border-0 font-weight-bold">
                                        {numberWithCommas(
                                          dashboardData.loan_summary.total_loan_amount?.toFixed(
                                            2
                                          )
                                        )}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </Col>
                            <Col md="6">
                              <h5 className="title mb-3">Recent Activity</h5>
                              <div className="d-flex justify-content-between">
                                <div className="w-50 pr-3">
                                  <h6 className="text-success small text-uppercase font-weight-bold mb-3">
                                    Joins
                                  </h6>
                                  {dashboardData.recent_activities.recent_joins
                                    .slice(0, 3)
                                    .map((emp, i) => (
                                      <div
                                        key={i}
                                        className="mb-3 p-2 rounded small"
                                        style={{
                                          background: isDark
                                            ? "rgba(255,255,255,0.03)"
                                            : "rgba(0,0,0,0.02)",
                                        }}
                                      >
                                        <div className="font-weight-bold">
                                          {emp.name}
                                        </div>
                                        <div className="text-muted text-xs">
                                          {new Date(
                                            emp.joining_date
                                          ).toLocaleDateString()}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                                <div className="w-50 pl-3">
                                  <h6 className="text-danger small text-uppercase font-weight-bold mb-3">
                                    Leaves
                                  </h6>
                                  {dashboardData.recent_activities.recent_leaves
                                    .slice(0, 3)
                                    .map((emp, i) => (
                                      <div
                                        key={i}
                                        className="mb-3 p-2 rounded small"
                                        style={{
                                          background: isDark
                                            ? "rgba(255,255,255,0.03)"
                                            : "rgba(0,0,0,0.02)",
                                        }}
                                      >
                                        <div className="font-weight-bold">
                                          {emp.name}
                                        </div>
                                        <div className="text-muted text-xs">
                                          {new Date(
                                            emp.leaving_date
                                          ).toLocaleDateString()}
                                        </div>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </TabPane>

                        {/* --- Tab: Trends --- */}
                        <TabPane tabId="trends">
                          {contributionTrends && (
                            <Row>
                              <Col md="12">
                                <h5 className="title mb-3">
                                  Monthly Growth Data
                                </h5>
                                <div className="table-responsive">
                                  <table className="table table-hover align-items-center">
                                    <thead>
                                      <tr>
                                        <th style={tableHeaderStyle}>Month</th>
                                        <th
                                          className="text-right"
                                          style={tableHeaderStyle}
                                        >
                                          Emp. Contrib.
                                        </th>
                                        <th
                                          className="text-right"
                                          style={tableHeaderStyle}
                                        >
                                          Empr. Contrib.
                                        </th>
                                        <th
                                          className="text-right"
                                          style={tableHeaderStyle}
                                        >
                                          Profit
                                        </th>
                                        <th
                                          className="text-right"
                                          style={tableHeaderStyle}
                                        >
                                          Total
                                        </th>
                                        <th
                                          className="text-right"
                                          style={tableHeaderStyle}
                                        >
                                          Growth
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {contributionTrends.monthly_trends.map(
                                        (trend: any, idx: number) => (
                                          <tr key={idx}>
                                            <td className="py-3">
                                              {trend.month}
                                            </td>
                                            <td className="text-right py-3">
                                              {numberWithCommas(
                                                trend.employee_contribution?.toFixed(
                                                  2
                                                )
                                              )}
                                            </td>
                                            <td className="text-right py-3">
                                              {numberWithCommas(
                                                trend.employer_contribution?.toFixed(
                                                  2
                                                )
                                              )}
                                            </td>
                                            <td className="text-right py-3">
                                              {numberWithCommas(
                                                trend.profit?.toFixed(2)
                                              )}
                                            </td>
                                            <td className="text-right font-weight-bold py-3">
                                              {numberWithCommas(
                                                trend.total?.toFixed(2)
                                              )}
                                            </td>
                                            <td
                                              className={`text-right py-3 ${
                                                parseFloat(trend.growth_rate) >=
                                                0
                                                  ? "text-success"
                                                  : "text-danger"
                                              }`}
                                            >
                                              {trend.growth_rate}%
                                            </td>
                                          </tr>
                                        )
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              </Col>
                            </Row>
                          )}
                        </TabPane>

                        {/* --- Tab: Loans --- */}
                        <TabPane tabId="loans">
                          {loansAnalytics && (
                            <>
                              <div
                                style={{
                                  height: "350px",
                                  marginBottom: "30px",
                                }}
                              >
                                {getLoanTrendsData() && (
                                  <Bar
                                    data={getLoanTrendsData()}
                                    options={barOptions}
                                  />
                                )}
                              </div>
                              <Row>
                                <Col md="6">
                                  <h5 className="title mb-3">
                                    Status Breakdown
                                  </h5>
                                  <div className="table-responsive">
                                    <table className="table align-items-center">
                                      <thead>
                                        <tr>
                                          <th style={tableHeaderStyle}>
                                            Status
                                          </th>
                                          <th
                                            className="text-right"
                                            style={tableHeaderStyle}
                                          >
                                            Count
                                          </th>
                                          <th
                                            className="text-right"
                                            style={tableHeaderStyle}
                                          >
                                            Amount
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {loansAnalytics.status_breakdown.map(
                                          (status: any, idx: number) => (
                                            <tr key={idx}>
                                              <td className="text-capitalize py-3">
                                                {status._id}
                                              </td>
                                              <td className="text-right py-3">
                                                {status.count}
                                              </td>
                                              <td className="text-right py-3">
                                                {numberWithCommas(
                                                  status.total_amount?.toFixed(
                                                    2
                                                  )
                                                )}
                                              </td>
                                            </tr>
                                          )
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </Col>
                                <Col md="6">
                                  <h5 className="title mb-3">
                                    Top Loan Holders
                                  </h5>
                                  <div className="table-responsive">
                                    <table className="table align-items-center">
                                      <thead>
                                        <tr>
                                          <th style={tableHeaderStyle}>
                                            Employee
                                          </th>
                                          <th
                                            className="text-right"
                                            style={tableHeaderStyle}
                                          >
                                            Count
                                          </th>
                                          <th
                                            className="text-right"
                                            style={tableHeaderStyle}
                                          >
                                            Remaining
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {loansAnalytics.top_loan_holders
                                          .slice(0, 5)
                                          .map((holder: any, idx: number) => (
                                            <tr key={idx}>
                                              <td className="py-3">
                                                {holder.employee_name}{" "}
                                                <small className="d-block text-muted">
                                                  {holder._id}
                                                </small>
                                              </td>
                                              <td className="text-right py-3">
                                                {holder.total_loans}
                                              </td>
                                              <td className="text-right text-warning py-3">
                                                {numberWithCommas(
                                                  holder.total_remaining?.toFixed(
                                                    2
                                                  )
                                                )}
                                              </td>
                                            </tr>
                                          ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </Col>
                              </Row>
                            </>
                          )}
                        </TabPane>

                        {/* --- Tab: Employees --- */}
                        <TabPane tabId="employees">
                          {topEmployees && (
                            <Row>
                              {[
                                {
                                  title: "Top Contributors",
                                  data: topEmployees.top_by_contribution,
                                  valKey: "total_contribution",
                                  color: "text-primary",
                                },
                                {
                                  title: "Top Profit Earners",
                                  data: topEmployees.top_by_profit,
                                  valKey: "total_profit",
                                  color: "text-success",
                                },
                                {
                                  title: "Top Loan Takers",
                                  data: topEmployees.top_by_loans,
                                  valKey: "total_loan_amount",
                                  color: "text-warning",
                                },
                              ].map((section, sIdx) => (
                                <Col md="4" key={sIdx} className="mb-4">
                                  <Card
                                    className="h-100 shadow-none"
                                    style={{
                                      background: "transparent",
                                      border: isDark
                                        ? "1px solid rgba(255,255,255,0.05)"
                                        : "1px solid rgba(0,0,0,0.05)",
                                      borderRadius: "12px",
                                    }}
                                  >
                                    <CardHeader className="border-0">
                                      <h5 className="card-category text-center font-weight-bold">
                                        {section.title}
                                      </h5>
                                    </CardHeader>
                                    <CardBody>
                                      <table className="table table-sm align-items-center">
                                        <tbody>
                                          {section.data
                                            ?.slice(0, 8)
                                            .map((emp: any, idx: number) => (
                                              <tr key={idx}>
                                                <td className="border-0 py-2">
                                                  <span
                                                    className="d-block text-truncate font-weight-bold"
                                                    style={{
                                                      maxWidth: "120px",
                                                      fontSize: "0.85rem",
                                                    }}
                                                    title={emp.employee_name}
                                                  >
                                                    {emp.employee_name}
                                                  </span>
                                                  <small className="text-muted">
                                                    {emp.folio_no}
                                                  </small>
                                                </td>
                                                <td
                                                  className={`text-right border-0 py-2 ${section.color}`}
                                                  style={{ fontWeight: 600 }}
                                                >
                                                  {numberWithCommas(
                                                    emp[
                                                      section.valKey
                                                    ]?.toFixed(0)
                                                  )}
                                                </td>
                                              </tr>
                                            ))}
                                        </tbody>
                                      </table>
                                    </CardBody>
                                  </Card>
                                </Col>
                              ))}
                            </Row>
                          )}
                        </TabPane>

                        {/* --- Tab: Funds --- */}
                        <TabPane tabId="funds">
                          <Row>
                            <Col md="12">
                              <div className="table-responsive">
                                <table className="table align-items-center table-hover">
                                  <thead>
                                    <tr>
                                      <th style={tableHeaderStyle}>
                                        Fund Name
                                      </th>
                                      <th
                                        className="text-right"
                                        style={tableHeaderStyle}
                                      >
                                        Count
                                      </th>
                                      <th
                                        className="text-right"
                                        style={tableHeaderStyle}
                                      >
                                        Share (%)
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {dashboardData.investment_distribution?.map(
                                      (fund: any, idx: number) => (
                                        <tr key={idx}>
                                          <td className="py-3 font-weight-bold">
                                            {fund._id || "Unspecified"}
                                          </td>
                                          <td className="text-right py-3">
                                            {fund.count}
                                          </td>
                                          <td className="text-right py-3">
                                            {(
                                              (fund.count /
                                                dashboardData.overview
                                                  .total_employees) *
                                              100
                                            ).toFixed(1)}
                                            %
                                          </td>
                                        </tr>
                                      )
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            </Col>
                          </Row>
                        </TabPane>
                      </TabContent>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </div>
          )}
        </Container>
      </div>
    </>
  );
};

export default CompanyDashboard;
